/**
 * components/toast.tsx — single toast surface for the whole app.
 * `useToast()` is a thin compatibility layer so call sites read naturally;
 * every method delegates to the shared `showToast`.
 *
 *   const toast = useToast();
 *   toast.success(msg)   toast.error(msg)   toast.warn(msg)   toast.info(msg)
 */
import { showToast } from './toastComponent';

export type ToastTone = 'success' | 'warn' | 'danger' | 'info';

export interface ToastOptions {
  tone?: ToastTone;
  title?: string;
  message?: string;
}

export interface ToastApi {
  (opts: ToastOptions | string): void;
  success: (msg: string, sub?: string) => void;
  error: (msg: string, sub?: string) => void;
  warn: (msg: string, sub?: string) => void;
  info: (msg: string, sub?: string) => void;
  dismiss: () => void;
}

function toType(tone?: ToastTone): 'success' | 'error' | 'info' {
  if (tone === 'danger' || tone === 'warn') return 'error';
  if (tone === 'info') return 'info';
  return 'success';
}

const api = ((opts: ToastOptions | string): void => {
  if (typeof opts === 'string') {
    showToast('success', opts);
    return;
  }
  const text1 = opts.title ?? opts.message ?? '';
  const text2 = opts.title ? opts.message : undefined;
  showToast(toType(opts.tone), text1, text2);
}) as ToastApi;

api.success = (msg, sub) => showToast('success', msg, sub);
api.error = (msg, sub) => showToast('error', msg, sub);
api.warn = (msg, sub) => showToast('error', msg, sub);
api.info = (msg, sub) => showToast('info', msg, sub);
api.dismiss = () => {
  /* react-native-toast-message auto-hides; no-op */
};

export function useToast(): ToastApi {
  return api;
}
